import React, { useEffect } from "react"
import styled from "styled-components";
import { useFeathers } from "../../app/util";
import * as d3 from 'd3'
import { Skeleton } from '@mui/material';

const margin = {
  top: 10,
  bottom: 10,
  left: 10,
  right: 10
}

const DISTRICT_FILL = '#036949';
const PRIORITY_STROKE = '#d4af37';

const PAGE_SIZE = 100;

const CoveragePlanMap = ({
  electionImplementation,
  onClickImplementationDistrict,
  className,
  style
}) => {
  const feathers = useFeathers();
  const stateId = electionImplementation?.state != null
    ? (electionImplementation.state?._id ?? electionImplementation.state)
    : null;
  const electionKey = electionImplementation?.electionKey ?? null;

  const [ loading, setLoading ] = React.useState(true);
  const [ stateGeoJSON, setStateGeoJSON ] = React.useState(null);
  const [ implementationDistrictsGeoJSON, setImplementationDistrictsGeoJSON ] = React.useState(null);
  const [ eauDistrictIds, setEauDistrictIds ] = React.useState([]);
  const [ priorityDistrictIds, setPriorityDistrictIds ] = React.useState([]);
  const [ initialized, setInitialized ] = React.useState(false);

  const pathGeneratorRef = React.useRef(null);
  const implementationDistrictsLayerRef = React.useRef(null);
  const stateLayerRef = React.useRef(null);

  const loadGeoJSON = async (idList, onSave, properties = {}, filterToType) => {
    if (!idList?.length) return;
    const chunkSize = 20;
    const numChunks = Math.ceil(idList.length / chunkSize);
    const chunks = Array.from({ length: numChunks }, (_, i) => idList.slice(i * chunkSize, (i + 1) * chunkSize));
    const districts = (await Promise.all(
      chunks.map(chunk => {
        return feathers.getService('districts').find({
          query: { _id: chunk, ...(filterToType ? { type: filterToType } : {}), $limit: chunk.length, $includeGeoDistrict: true }
        })
      })
    )).map(d => d.data).flat(1)

    const areaDistrictsAsFeatures = districts.map(d =>
      formatGeoJSON(d.geoDistrict?.boundary || [], {
        "NAME": d?.longName,
        "_id": d._id,
        type: d.type,
        ...properties
      })
    );

    onSave({
      "type": "FeatureCollection",
      "features": areaDistrictsAsFeatures
    })
  }

  // Load implementation-districts (paginated) for EAU districts; derive district ids and priority ids.
  useEffect(() => {
    if (!feathers || !electionKey) return;
    let cancelled = false;
    (async () => {
      const service = feathers.getService('implementation-districts');
      let skip = 0;
      let all = [];
      while (true) {
        const res = await service.find({
          query: {
            election: electionKey,
            active: true,
            contentType: 'races',
            districtType: 'county',
            $limit: PAGE_SIZE,
            $skip: skip
          }
        });
        const data = res.data || [];
        all = all.concat(data);
        if (cancelled) return;
        if (data.length < PAGE_SIZE) break;
        skip += PAGE_SIZE;
      }
      const districtIds = [...new Set(all.map(d => d.district).filter(Boolean))];
      const priorityIds = [...new Set(all.filter(d => d.isPriority).map(d => d.district).filter(Boolean))];
      if (!cancelled) {
        setEauDistrictIds(districtIds);
        setPriorityDistrictIds(priorityIds);
      }
    })();
    return () => { cancelled = true; };
  }, [ electionKey, feathers ]);

  // Load geoJSON for EAU district ids.
  useEffect(() => {
    if (!feathers) return;
    if (eauDistrictIds.length) {
      loadGeoJSON(eauDistrictIds, setImplementationDistrictsGeoJSON);
    } else {
      setImplementationDistrictsGeoJSON({ type: 'FeatureCollection', features: [] });
    }
  }, [ eauDistrictIds, feathers ]);

  useEffect(() => {
    if (feathers && stateId) {
      loadGeoJSON([stateId], setStateGeoJSON);
    }
  }, [ stateId, feathers ]);

  useEffect(() => {
    if (stateGeoJSON) setLoading(false);
  }, [ stateGeoJSON ]);

  const isPriorityDistrict = (d) => {
    return priorityDistrictIds.some(p => `${p}` === `${d?.properties?._id}`);
  }

  useEffect(() => {
    if(loading || initialized) return;

    let width = parseInt(d3.select('.viz').style('width'));
    let height = parseInt(d3.select('.viz').style('height'));
    width = width - margin.left - margin.right;

    console.log({
      width,
      height
    })

    const svg = d3.select('.viz').append('svg')
      .attr('class', 'center-container')
      .attr('height', height + margin.top + margin.bottom)
      .attr('width', width + margin.left + margin.right);

    svg.append('rect')
      .attr('class', 'background center-container')
      .attr('height', height + margin.top + margin.bottom)
      .attr('width', width + margin.left + margin.right)
      
    // Not using a projection -- note: this means it will look a bit squished or stretched
    const projection = d3
      .geoIdentity()
      .reflectY(true)
      .fitExtent([[0, 0], [width, height]], stateGeoJSON);


    // Creating path generator fromt the projecttion created above.
    const pathGenerator = d3.geoPath()
      .projection(projection);

    pathGeneratorRef.current = pathGenerator;

    // Creating the container
    const g = svg.append("g")
      .attr('class', 'center-container center-items us-state')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)

    // Creating state layer
    const stateLayer = g.append("g")
      .attr("id", "state-boundary");

    stateLayerRef.current = stateLayer

    // Layer for implementation districts (EAU)
    const implementationDistrictsLayer = g.append("g")
      .attr("id", "implementation-districts");
    implementationDistrictsLayerRef.current = implementationDistrictsLayer;

    setInitialized(true)
  }, [ loading, initialized ]);

  useEffect(() => {
    const pathGenerator = pathGeneratorRef.current;
    const stateLayer = stateLayerRef.current;
    const implementationDistrictsLayer = implementationDistrictsLayerRef.current;

    if(!pathGenerator || !stateLayer || !implementationDistrictsLayer) return;

    if(stateGeoJSON) {
      stateLayer
        .selectAll('path')
        .data(stateGeoJSON.features)
        .join('path')
        .attr("key", feature => {
          return feature.properties?.NAME
        })
        .attr("fill", '#FFFFFF')
        .style('stroke', '#888888')
        .style('stroke-width', '1')
        .attr('d', pathGenerator)
        .attr("class", "state-boundary")
        .exit().remove()
    }

    if(implementationDistrictsGeoJSON) {
      implementationDistrictsLayer
        .selectAll('path')
        .data(implementationDistrictsGeoJSON.features)
        .join('path')
        .attr("key", feature => {
          return feature.properties?.NAME
        })
        .attr("class", "implementation-district")
        .attr("fill", DISTRICT_FILL)
        .style('stroke', (d) => isPriorityDistrict(d) ? PRIORITY_STROKE : '#eeeeee')
        .style('stroke-width', (d) => isPriorityDistrict(d) ? '2' : '1')
        .attr('d', pathGenerator)
        .on('click', (e, d) => {
          if(onClickImplementationDistrict) onClickImplementationDistrict(d?.properties?._id)
        })
        .exit().remove()
    }
  }, [ stateGeoJSON, implementationDistrictsGeoJSON, initialized, priorityDistrictIds ])

  return (
    (<Wrapper className={className} style={style}>
      {
        loading &&
        <Skeleton variant="rectangular" width={500} height={350} />
      }
      <div class="viz" style={style}>
      </div>
    </Wrapper>)
  );
}





const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: 600px;
  width: 600px;

  .viz {
    width: 100%;
    height: 100%;
  }
  .background {
    fill: none;
    pointer-events: all;
  }

  #implementation-districts, #priority-districts {
    fill: none;
    stroke: #fff;
    stroke-linejoin: round;
    stroke-width: 1.5px;
  }

  #implementation-districts .active {
    display: none;
  }

  .state-boundary {
    stroke: #fff;
    stroke-width: 0.5px;
  }

  .implementation-district:hover, .priority-district:hover {
    fill: #002b5b;
  }
`

const getArrayDepth = (array) => Array.isArray(array) ?
    1 + Math.max(0, ...array.map(item => getArrayDepth(item))) :
    0;

export const determineShapeFromCoordinates = (coordinates) => {
  if (coordinates === null) return null
  const depth = getArrayDepth(coordinates)
  let type;
  switch(depth) {
    case 1:
      type = "Point";
      break;
    case 2:
      type = "LineString";
      break;
    case 3:
      type = "Polygon";
      break;
    case 4:
      type = "MultiPolygon";
      break;
    default:
      throw new Error('Unsupported shape')
  }
  return type;
}

export const formatGeoJSON = (coordinates, properties) => {
  if ( coordinates === null) return null
  const geometry = {
    type: determineShapeFromCoordinates(coordinates),
    coordinates,
  }

  return {
    "type": "Feature",
    "geometry": geometry,
    "properties": (properties || {})
  }
}

export { CoveragePlanMap };